import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Intern", "Probation"];
export const emptyDetails = {
  name: "", employee_code: "", phone: "", email: "", designation: "", department: "", joining_date: "",
  work_location: "", store_id: "", reporting_manager: "", regional_manager: "", employment_type: "Full-time",
};

const Field = ({ label, children, span }) => (
  <div className={`space-y-1.5 ${span ? "md:col-span-2" : ""}`}><Label className="eyebrow">{label}</Label>{children}</div>
);

const PeopleSelect = ({ value, onChange, people, testid }) => (
  <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>
      <SelectItem value="__none__">— Not set —</SelectItem>
      {people.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
      {value && !people.some((u) => u.name === value) && <SelectItem value={value}>{value}</SelectItem>}
    </SelectContent>
  </Select>
);

export const EmployeeDetailsForm = ({ f, setF }) => {
  const { user, stores } = useAuth();
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const storeOptions = (stores || []).filter((s) => s.active !== false && (user?.role !== "regional_manager" || (user?.store_ids || []).includes(s.id)));
  const managers = users.filter((u) => ["manager", "admin"].includes(u.role) && u.active !== false);
  const rms = users.filter((u) => u.role === "regional_manager" && u.active !== false);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="emp-details-form">
      <Field label="Employee Name *"><Input value={f.name} onChange={set("name")} data-testid="emp-name" /></Field>
      <Field label="Employee ID">
        <Input value={f.employee_code} onChange={(e) => setF({ ...f, employee_code: e.target.value.toUpperCase() })} disabled={!!f._existing}
          placeholder="Auto (EMP0001) — or type your own" className="font-mono" data-testid="emp-code" />
      </Field>
      <Field label="Contact Number *"><Input value={f.phone} inputMode="numeric" onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="font-mono" data-testid="emp-phone" /></Field>
      <Field label="Email (employee login)"><Input type="email" value={f.email} onChange={set("email")} data-testid="emp-email" /></Field>
      <Field label="Designation *"><Input value={f.designation} onChange={set("designation")} data-testid="emp-designation" /></Field>
      <Field label="Department"><Input value={f.department} onChange={set("department")} data-testid="emp-department" /></Field>
      <Field label="Joining Date *"><Input type="date" value={f.joining_date} onChange={set("joining_date")} data-testid="emp-joining-date" /></Field>
      <Field label="Work Location"><Input value={f.work_location} onChange={set("work_location")} placeholder="e.g. Pune office" data-testid="emp-work-location" /></Field>
      <Field label={`Store ${user?.role === "regional_manager" ? "*" : ""}`}>
        <Select value={f.store_id || "__none__"} onValueChange={(v) => setF({ ...f, store_id: v === "__none__" ? "" : v })}>
          <SelectTrigger data-testid="emp-store"><SelectValue placeholder="No store" /></SelectTrigger>
          <SelectContent>
            {user?.role !== "regional_manager" && <SelectItem value="__none__">No store</SelectItem>}
            {storeOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} — {s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Reporting Manager"><PeopleSelect value={f.reporting_manager} onChange={(v) => setF({ ...f, reporting_manager: v })} people={managers} testid="emp-reporting-manager" /></Field>
      <Field label="Regional Manager"><PeopleSelect value={f.regional_manager} onChange={(v) => setF({ ...f, regional_manager: v })} people={rms} testid="emp-regional-manager" /></Field>
      <Field label="Employment Type *">
        <Select value={f.employment_type} onValueChange={(v) => setF({ ...f, employment_type: v })}>
          <SelectTrigger data-testid="emp-employment-type"><SelectValue /></SelectTrigger>
          <SelectContent>{EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    </div>
  );
};

export const validateDetails = (f, role) => {
  if (!f.name.trim()) return "Employee name is required";
  if (f.phone.length !== 10) return "Contact number must be 10 digits";
  if (!f.designation.trim()) return "Designation is required";
  if (!f.joining_date) return "Joining date is required";
  if (!f.employment_type) return "Employment type is required";
  if (role === "regional_manager" && !f.store_id) return "Pick the employee's store";
  return "";
};
